import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { gameToPixel, getRadarImageUrl, hasMapTransform } from '../../utils/mapTransforms';
import { inflateBase64 } from '../../utils/inflate';

/** Canvasens storlek i pixlar (radarbilderna är 1024 px, ritas nedskalat). */
const SIZE = 640;

/** Kod per nav-area i ramdatan → CSS-variabel för färgen (0 = ingen kontroll ritas inte). */
const CODE_VARS = { 1: '--mc-ct', 2: '--mc-t', 3: '--mc-contested' };

/** Spelarsida i ramdatan → CSS-variabel. */
const SIDE_VARS = { 1: '--mc-ct', 2: '--mc-t' };

/**
 * Läser temats färger från panelens CSS-variabler, så att kartan följer valt tema.
 *
 * @param {HTMLElement} el Element där `--mc-*` är definierade.
 * @returns {Object<string, string>} Färg per CSS-variabel.
 */
function readColors(el) {
  const style = getComputedStyle(el);
  const out = {};
  ['--mc-ct', '--mc-t', '--mc-contested', '--bg-card', '--bg-secondary', '--text-primary',
    '--text-secondary', '--border-color'].forEach((name) => {
    out[name] = style.getPropertyValue(name).trim();
  });
  return out;
}

/** Pixelradie (i canvasens 640-skala) inom vilken en prick räknas som träffad av muspekaren. */
const HOVER_RADIUS = 14;

/**
 * Ritar en tooltip (namn + HP) bredvid en spelarprick direkt i canvasen.
 *
 * Canvas i stället för en HTML-ruta: positionen ändras varje sekund under uppspelning,
 * och sajten använder bara CSS-klasser (ingen inline-stil för position).
 *
 * @param {CanvasRenderingContext2D} ctx Canvasens kontext.
 * @param {number} px Prickens x i pixlar.
 * @param {number} py Prickens y i pixlar.
 * @param {string} title Spelarens namn.
 * @param {string} detail Andra raden (HP).
 * @param {Object<string, string>} colors Temafärger från `readColors`.
 * @param {string} sideColor Spelarens lagfärg (markör framför namnet).
 * @returns {void}
 */
function drawTooltip(ctx, px, py, title, detail, colors, sideColor) {
  ctx.save();
  ctx.globalAlpha = 1;
  ctx.font = '600 15px sans-serif';
  const titleW = ctx.measureText(title).width;
  ctx.font = '13px sans-serif';
  const w = Math.max(titleW + 22, ctx.measureText(detail).width) + 20;
  const h = 46;
  let x = px + 12;
  let y = py - h - 8;
  if (x + w > SIZE - 4) x = px - w - 12;
  if (y < 4) y = py + 12;
  ctx.fillStyle = colors['--bg-secondary'] || '#12121a';
  ctx.strokeStyle = colors['--border-color'] || '#2a2a3a';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, 6);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = sideColor;
  ctx.beginPath();
  ctx.arc(x + 15, y + 16, 5, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = colors['--text-primary'] || '#fff';
  ctx.font = '600 15px sans-serif';
  ctx.textBaseline = 'middle';
  ctx.fillText(title, x + 26, y + 16);
  ctx.fillStyle = colors['--text-secondary'] || '#aaa';
  ctx.font = '13px sans-serif';
  ctx.fillText(detail, x + 10, y + 34);
  ctx.restore();
}

/**
 * MapControlMap — radarbild där varje nav-area är färgad efter vilken sida som
 * håller den, vid en vald sekund i rundan, plus prickar för levande spelare.
 *
 * Geometrin hämtas en gång per match och karta, ramarna per runda (och cachas).
 * Kartor utan radarbild/koordinatomvandling i `mapTransforms.js` visar en kort
 * notis i stället för kartan. Håller man muspekaren över en spelarprick visas namn
 * och HP; tooltipen följer spelaren under uppspelning.
 *
 * @param {{
 *   matchId: string|number,
 *   mapNumber: string|number,
 *   roundNumber: number,
 *   method: 'reachability'|'vision',
 *   sampleIndex: number,
 *   playerNames?: Object<string, string>,
 * }} props Match, karta, runda, modell, vald sekund (index i rundans sampel) och sajtens
 *   spelarnamn per steamid (fallback: namnet i spelet ur ramdatan).
 * @returns {JSX.Element|null} Kartvyn, en notis, eller null medan inget finns att visa.
 */
const MapControlMap = ({ matchId, mapNumber, roundNumber, method, sampleIndex, playerNames = {} }) => {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  // Index i rundans roster för spelaren under muspekaren (följer spelaren över tid).
  const [hovered, setHovered] = useState(null);
  const [geometry, setGeometry] = useState(null);
  const [radar, setRadar] = useState(null);
  const [frames, setFrames] = useState({});
  const [failed, setFailed] = useState(false);

  // Geometri + radarbild, en gång per match och karta.
  useEffect(() => {
    let cancelled = false;
    setGeometry(null);
    setFrames({});
    setFailed(false);
    fetch(`${API_URL}/api/stats/match/${matchId}/map-control/geometry?map=${mapNumber}`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (data.error || !data.areas) { setFailed(true); return; }
        setGeometry(data);
        const url = getRadarImageUrl(data.map_tag);
        if (url) {
          const img = new Image();
          img.onload = () => { if (!cancelled) setRadar(img); };
          img.src = url;
        }
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [matchId, mapNumber]);

  // Ny runda → ingen markerad spelare (rosterindex gäller per runda).
  useEffect(() => { setHovered(null); }, [roundNumber]);

  // Ramar för vald runda (cachas per runda).
  useEffect(() => {
    if (!geometry || frames[roundNumber]) return undefined;
    let cancelled = false;
    fetch(`${API_URL}/api/stats/match/${matchId}/map-control/round/${roundNumber}?map=${mapNumber}`)
      .then((res) => res.json())
      .then(async (data) => {
        if (cancelled || data.error) return;
        const entry = {
          reachability: data.reachability ? await inflateBase64(data.reachability) : null,
          vision: data.vision ? await inflateBase64(data.vision) : null,
          players: data.players || [],
          roster: data.roster || [],
        };
        if (!cancelled) setFrames((prev) => ({ ...prev, [roundNumber]: entry }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [geometry, roundNumber, matchId, mapNumber, frames]);

  // Polygonerna i pixelkoordinater (beror bara på geometrin).
  const pixelAreas = useMemo(() => {
    if (!geometry || !hasMapTransform(geometry.map_tag)) return null;
    return geometry.areas.map((poly) => poly.map(([x, y]) => gameToPixel(x, y, geometry.map_tag, SIZE)));
  }, [geometry]);

  // Ritordning: lägst först, så att övre våningar (Nuke, Vertigo) hamnar överst.
  const drawOrder = useMemo(() => {
    if (!geometry) return [];
    const z = geometry.z || [];
    return geometry.areas.map((_, i) => i).sort((a, b) => (z[a] || 0) - (z[b] || 0));
  }, [geometry]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const frame = frames[roundNumber];
    if (!canvas || !pixelAreas) return;
    const ctx = canvas.getContext('2d');
    const colors = readColors(canvas.parentElement);
    ctx.clearRect(0, 0, SIZE, SIZE);
    if (radar) {
      ctx.globalAlpha = 1;
      ctx.drawImage(radar, 0, 0, SIZE, SIZE);
    }
    const labels = frame && frame[method];
    if (labels) {
      const n = pixelAreas.length;
      const offset = Math.min(sampleIndex, Math.floor(labels.length / n) - 1) * n;
      ctx.globalAlpha = 0.55;
      for (const i of drawOrder) {
        const color = colors[CODE_VARS[labels[offset + i]]];
        const poly = pixelAreas[i];
        if (!color || poly.length < 3) continue;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(poly[0][0], poly[0][1]);
        for (let k = 1; k < poly.length; k += 1) ctx.lineTo(poly[k][0], poly[k][1]);
        ctx.closePath();
        ctx.fill();
      }
    }
    const players = frame && frame.players[Math.min(sampleIndex, frame.players.length - 1)];
    if (players) {
      ctx.globalAlpha = 1;
      let tip = null;
      players.forEach(([x, y, side, idx, hp]) => {
        const [px, py] = gameToPixel(x, y, geometry.map_tag, SIZE);
        const isHovered = idx !== undefined && idx === hovered;
        ctx.fillStyle = colors[SIDE_VARS[side]] || '#fff';
        ctx.lineWidth = isHovered ? 3 : 2;
        ctx.strokeStyle = isHovered ? (colors['--text-primary'] || '#fff') : (colors['--bg-card'] || '#000');
        ctx.beginPath();
        ctx.arc(px, py, isHovered ? 8 : 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        if (isHovered) tip = { px, py, idx, hp, side };
      });
      if (tip && frame.roster[tip.idx]) {
        const [steamid, gameName] = frame.roster[tip.idx];
        const name = playerNames[steamid] || gameName || steamid;
        drawTooltip(ctx, tip.px, tip.py, name, t('mapControl.hp', { hp: tip.hp }), colors,
          colors[SIDE_VARS[tip.side]] || '#fff');
      }
    }
  }, [pixelAreas, drawOrder, radar, frames, roundNumber, method, sampleIndex, geometry, hovered, playerNames, t]);

  /**
   * Hittar spelarpricken närmast muspekaren (inom HOVER_RADIUS) och markerar den.
   *
   * @param {React.MouseEvent<HTMLCanvasElement>} event Musrörelsen över canvasen.
   * @returns {void}
   */
  const handleMouseMove = (event) => {
    const frame = frames[roundNumber];
    const players = frame && frame.players[Math.min(sampleIndex, frame.players.length - 1)];
    if (!players || !geometry) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const mx = ((event.clientX - rect.left) / rect.width) * SIZE;
    const my = ((event.clientY - rect.top) / rect.height) * SIZE;
    let best = null;
    let bestDist = HOVER_RADIUS;
    players.forEach(([x, y, , idx]) => {
      if (idx === undefined) return;
      const [px, py] = gameToPixel(x, y, geometry.map_tag, SIZE);
      const d = Math.hypot(px - mx, py - my);
      if (d <= bestDist) { best = idx; bestDist = d; }
    });
    setHovered(best);
  };

  if (failed || !geometry) return null;
  if (!pixelAreas) {
    return <p className="map-control-map-note">{t('mapControl.mapUnavailable')}</p>;
  }
  return (
    <div className="map-control-map">
      <canvas
        ref={canvasRef}
        width={SIZE}
        height={SIZE}
        className={`map-control-map-canvas ${hovered !== null ? 'is-hovering' : ''}`}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHovered(null)}
        role="img"
        aria-label={t('mapControl.mapAria', { round: roundNumber })}
      />
      {!frames[roundNumber] && <p className="map-control-map-note">{t('mapControl.loadingMap')}</p>}
    </div>
  );
};

export default MapControlMap;
